import type React from 'react'
import { cn } from '../../lib/utils'

// ── The one section surface ─────────────────────────────────────────────────
//
// A "section" is an eyebrow-labelled block of related settings or facts. Every
// one of them should look identical, and before this they did not: the same
// page carried rounded-lg / -xl / -2xl, p-3 / p-4 / p-5, some with a fill and
// some without, and — the fault under all of it — a border colour that does
// not draw.
//
// THE BORDER TOKEN IS THE POINT. This design system is borderless by default:
// index.css routes `--border` through `--border-base` to `--card`, so
// `border-border` paints the edge in exactly the surface colour and renders
// invisible. That is correct for decorative dividers and wrong for the one
// thing whose whole job is to be seen. `--border-control` is the system's
// existing APPROVED visible border, with a value defined for both themes
// (84% light / 22% dark), so this stays right in dark mode instead of
// hardcoding a grey that would not.
//
// NESTING. A section surface must not contain another one. Card-on-card gives
// two identical fills with nothing for the inner edge to sit against, which
// reads as one slab — the exact problem these borders exist to solve. A group
// OF sections is a SectionLabel plus spacing, never a bordered box around
// bordered boxes.
export const SECTION_SURFACE =
  'rounded-xl border border-[hsl(var(--border-control))] bg-card'

/** A section's card surface. `className` carries padding/layout per use site —
 *  row-list sections pass `divide-y` instead of padding. */
export function SectionSurface({
  className, children,
}: { className?: string; children: React.ReactNode }) {
  return <div className={cn(SECTION_SURFACE, className)}>{children}</div>
}

/** The canonical section eyebrow.
 *
 *  mb-1.5, deliberately tighter than the gap BETWEEN sections: the label
 *  belongs to the card directly beneath it, so its distance to that card must
 *  read as clearly smaller than its distance to the section above. At equal
 *  spacing the labels float between two blocks and it is ambiguous which one
 *  each names. */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
      {children}
    </p>
  )
}

/** Vertical rhythm for a stack of sections. Sections are their own cards on the
 *  page, so the gap between them is the only thing grouping their contents. */
export const SECTION_STACK = 'space-y-7'
